/* component-cart-drawer.js — <cart-drawer>
   Native <dialog> drawer. Focus trap + Escape close are provided by showModal().
   Behaviour:
   - Opens on kk:drawer:open {id:'cart'} and automatically after add-to-cart
     (kk:cart:updated payload carrying `added`).
   - Re-renders its own inner content from the Section Rendering API payload that
     the CartStore attaches on kk:cart:updated (payload.sections[sectionId]).
   - Closes on the close button, backdrop click, Escape (native), and empty-state
     "continue shopping" nav.
   - Talks to the cart ONLY through window.KK.cart (never fetches /cart itself),
     so it publishes nothing except the drawer open/close events.

   UPSELL (native, Shopify Product Recommendations powered — no apps):
   - On open / after every re-render, fetches recommendations for the FIRST cart
     item via the recommendations route
     (?section_id=<this section>&product_id=…&intent=complementary → falls back to
     related), extracts the [data-cart-upsell] block and injects the cards into the
     live drawer. Products already in the cart are filtered out.
   - Each suggestion's add button adds via KK.cart.add with the line-item property
     _upsell:'cart' and fires GA4 events through KK.track: kk_upsell_view /
     kk_upsell_click / kk_upsell_add.

   ERROR CONTRACT (buy-path audit 2026-07-31). Every window.KK.cart method rejects
   with an Error carrying `userMessage` (safe, already-localized German) and `code`
   ('network'|'timeout'|'unavailable'|'http'|'unknown'). This component renders ONLY
   err.userMessage — never err.message / raw exception text — falling back to the
   section's own data-error-generic string. KK.cart owns the 15s timeout; we add none.
   Every busy flag set here is cleared in a finally(), and no rejection is swallowed.
   ES module, one component per file. */

const SECTION_ID_ATTR = 'data-section-id';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute(SECTION_ID_ATTR);
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    // Upsell config (from the section markup).
    this._recUrl = this.getAttribute('data-rec-url') || '/recommendations/products';
    this._upsellIntent = this.getAttribute('data-upsell-intent') || 'complementary';
    this._upsellLimit = parseInt(this.getAttribute('data-upsell-limit'), 10) || 6;
    this._upsellEnabled = this.getAttribute('data-upsell-enabled') !== '0';
    this._upsellForProduct = null; // product id the cached upsell HTML belongs to
    this._upsellHTML = null;       // cached inner HTML of [data-cart-upsell]
    this._viewedSig = null;        // item-id signature we already fired kk_upsell_view for
    this._expressHTML = null;      // cached dynamic-checkout buttons (survive re-render)

    // Repaint sequencing (buy-path audit 2026-07-31 — "racing quantity taps: an older
    // section render lands last and rolls the whole drawer back"). Every repaint-
    // triggering action bumps _gen; an in-flight render whose token is stale is
    // discarded, and its request is aborted the moment a newer one starts.
    this._gen = 0;
    this._renderAbort = null;
    this._notice = null;           // buyer-facing failure text, re-applied after re-render

    this._bindStaticListeners();
    this._cacheExpress();
    this._hydrateDiscount();

    if (window.KK && window.KK.pubsub) {
      this._unsubOpen = window.KK.pubsub.subscribe('kk:drawer:open', (p) => {
        if (!p || p.id === 'cart') { this.open(); this._loadUpsell(); }
      });
      this._unsubClose = window.KK.pubsub.subscribe('kk:drawer:close', (p) => {
        if (!p || p.id === 'cart') this.close();
      });
      this._unsubUpdated = window.KK.pubsub.subscribe('kk:cart:updated', (p) => this._onCartUpdated(p));
    }
  }

  disconnectedCallback() {
    if (this._unsubOpen) this._unsubOpen();
    if (this._unsubClose) this._unsubClose();
    if (this._unsubUpdated) this._unsubUpdated();
  }

  /* Listeners on elements that persist across re-renders (the <dialog> is replaced
     wholesale? No — we only replace inner .s-cart-drawer__panel, so the dialog and
     backdrop stay). We (re)bind panel-level listeners via delegation on the dialog. */
  _bindStaticListeners() {
    // Delegated click handling survives inner re-renders.
    this.dialog.addEventListener('click', (e) => {
      // Backdrop click: dialog is the click target itself (outside the panel).
      if (e.target === this.dialog) { this.close(); return; }
      if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
      if (e.target.closest('[data-cart-close-nav]')) { this.close(); /* allow nav */ return; }

      // ---- Discount (delegated so it survives re-render) ----
      const dRemove = e.target.closest('[data-discount-remove]');
      if (dRemove) { e.preventDefault(); this._removeDiscount(); return; }

      // ---- Line-item remove (trash icon). The anchor's href is the /cart/change
      // no-JS fallback, which NAVIGATES to the cart page — we're slide-cart-only,
      // so intercept and remove via the CartStore; kk:cart:updated re-renders. ----
      const lineRemove = e.target.closest('[data-line-remove]');
      if (lineRemove) {
        e.preventDefault();
        if (!window.KK || !window.KK.cart) return;
        // Guard symmetry (buy-path audit 2026-07-31): the guard reads BOTH flags this
        // handler sets. It is an <a>, so `disabled` does not exist — aria-disabled is
        // the anchor's equivalent and is set/read alongside aria-busy.
        if (lineRemove.hasAttribute('aria-busy') || lineRemove.getAttribute('aria-disabled') === 'true') return;
        lineRemove.setAttribute('aria-busy', 'true');
        lineRemove.setAttribute('aria-disabled', 'true');
        this._clearNotice();
        window.KK.cart.change(lineRemove.getAttribute('data-line-remove'), 0)
          .catch((err) => {
            // The mutation may still have landed server-side (429 bot-wall / opaque
            // failure), so never trust the panel afterwards — show the store's own
            // message and reconcile against the real cart.
            console.warn('[kk] cart drawer: line remove failed', err && err.code);
            this._notify(err);
            return this._reconcile(++this._gen);
          })
          // finally(), not catch(): on a SUCCESS whose repaint never happens the node
          // survives with aria-busy set and the guard above kills the control forever.
          .finally(() => {
            if (lineRemove.isConnected) {
              lineRemove.removeAttribute('aria-busy');
              lineRemove.removeAttribute('aria-disabled');
            }
          });
        return;
      }

      // ---- Upsell (delegated so it survives injection + re-render) ----
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) { e.preventDefault(); this._onUpsellAdd(addBtn); return; }
      // Inline variant dropdown (doc 6 — replaces the native select's OS sheet).
      const ddToggle = e.target.closest('[data-upsell-dd-toggle]');
      if (ddToggle) {
        e.preventDefault();
        const dd = ddToggle.closest('[data-upsell-dd]');
        const willOpen = !dd.classList.contains('is-open');
        this._closeAllDd();
        if (willOpen) {
          dd.classList.add('is-open');
          ddToggle.setAttribute('aria-expanded', 'true');
          const list = dd.querySelector('[data-upsell-dd-list]');
          if (list) {
            list.hidden = false;
            // Clamp the sheet inside the drawer panel: right-anchored to a field
            // near the panel's left edge it would overflow and get clipped
            // (owner desktop screenshot 2026-07-29). Shift it right/left just
            // enough to keep a 12px inset on both sides.
            list.style.right = '';
            const panel = this.dialog.querySelector('.s-cart-drawer__panel') || this.dialog;
            const pr = panel.getBoundingClientRect();
            let lr = list.getBoundingClientRect();
            let shift = 0;
            const overLeft = (pr.left + 12) - lr.left;
            if (overLeft > 0) shift = -overLeft;
            lr = list.getBoundingClientRect();
            const overRight = (lr.right - shift) - (pr.right - 12);
            if (overRight > 0) shift += overRight;
            if (shift) list.style.right = shift + 'px';
          }
        }
        return;
      }
      const ddOpt = e.target.closest('[data-upsell-dd-option]');
      if (ddOpt) {
        e.preventDefault();
        const dd = ddOpt.closest('[data-upsell-dd]');
        dd.setAttribute('data-selected-variant', ddOpt.getAttribute('data-variant-id') || '');
        const lSize = dd.querySelector('[data-upsell-dd-size]');
        const lPrice = dd.querySelector('[data-upsell-dd-price]');
        if (lSize) lSize.textContent = ddOpt.getAttribute('data-size') || ddOpt.textContent.trim();
        if (lPrice) lPrice.textContent = ddOpt.getAttribute('data-price') || '';
        const label = dd.querySelector('[data-upsell-dd-label]');
        if (label) label.textContent = ddOpt.textContent.trim();
        dd.querySelectorAll('[data-upsell-dd-option]').forEach((o) => {
          o.setAttribute('aria-selected', o === ddOpt ? 'true' : 'false');
        });
        this._closeAllDd();
        return;
      }
      const link = e.target.closest('[data-upsell-link]');
      if (link) { this._onUpsellClick(link); return; } // do NOT preventDefault — allow nav
      const prev = e.target.closest('[data-upsell-prev]');
      if (prev) { e.preventDefault(); this._scrollUpsell(prev, -1); return; }
      const next = e.target.closest('[data-upsell-next]');
      if (next) { e.preventDefault(); this._scrollUpsell(next, 1); return; }
      // Any other tap inside the drawer closes an open variant dropdown.
      this._closeAllDd();
    });
    // Escape with an open variant dropdown closes the dropdown, not the drawer.
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dialog.querySelector('[data-upsell-dd].is-open')) {
        e.preventDefault();
        e.stopPropagation();
        this._closeAllDd();
      }
    });
    // Discount form submit (Enter or "Anwenden"). Delegated + guarded so it only
    // touches [data-discount-form] — the sibling {% form 'cart' %} checkout submit
    // is left to proceed natively.
    this.dialog.addEventListener('submit', (e) => {
      const df = e.target.closest('[data-discount-form]');
      if (df) { e.preventDefault(); this._applyDiscount(df); }
    });
    // A failed line-quantity change used to be completely silent: <quantity-input>
    // reverts the field from the LAST KNOWN cart and dispatches kk:qty:error, which
    // had zero listeners anywhere in the theme (buy-path audit 2026-07-31). The drawer
    // owns the only surface that can show it, so it listens, prints the store's
    // localized message and reconciles the panel against the real cart.
    this.dialog.addEventListener('kk:qty:error', (e) => {
      const err = e && e.detail && e.detail.error;
      console.warn('[kk] cart drawer: quantity change failed', err && err.code);
      this._notify(err);
      this._reconcile(++this._gen);
    });
    // Native Escape → dialog fires 'close'; publish our event so header etc. sync.
    this.dialog.addEventListener('close', () => {
      if (window.KK && window.KK.pubsub) window.KK.pubsub.publish('kk:drawer:close', { id: 'cart' });
    });
  }

  /* Re-entry guards (_opening/_closing): this component both PUBLISHES and
     SUBSCRIBES to kk:drawer:open/close. Without a guard, open() → publish →
     own subscriber → open() recurses forever (same for close via the native
     dialog 'close' event) — an unbounded synchronous loop that crashes the
     tab the moment add-to-cart auto-opens the drawer. Publish only on an
     actual closed→open (or open→closed) transition, and never re-enter. */
  open() {
    if (this._opening) return;
    this._opening = true;
    try {
      const wasOpen = this.dialog.open || this.dialog.hasAttribute('open');
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      if (!wasOpen && window.KK && window.KK.pubsub) {
        window.KK.pubsub.publish('kk:drawer:open', { id: 'cart' });
      }
      // Move focus to the close button for keyboard users.
      const closeBtn = this.dialog.querySelector('[data-cart-close]');
      if (closeBtn) closeBtn.focus();
    } finally {
      this._opening = false;
    }
  }

  close() {
    if (this._closing) return;
    this._closing = true;
    try {
      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close(); // native 'close' event publishes kk:drawer:close
      } else if (this.dialog.hasAttribute('open')) {
        this.dialog.removeAttribute('open');
        if (window.KK && window.KK.pubsub) {
          window.KK.pubsub.publish('kk:drawer:close', { id: 'cart' });
        }
      }
    } finally {
      this._closing = false;
    }
  }

  _onCartUpdated(payload) {
    if (!payload) return;
    // This mutation supersedes every repaint already in flight: bump the token and
    // abort the older section request so its (older) HTML can never win the race.
    const gen = ++this._gen;
    this._abortRender();
    this._clearNotice(); // a mutation the store confirmed clears the previous failure
    // Prefer the section HTML the CartStore already fetched (add-to-cart passes
    // sections). If it isn't there (e.g. a quantity change from inside the drawer,
    // which doesn't request sections), self-fetch our section so line items,
    // subtotal and the free-shipping bar all re-render consistently.
    const html = this._extractSectionHTML(payload.sections);
    if (html) {
      this._render(html);
      if (payload.added) this.open();
      this._loadUpsell();
    } else {
      this._selfRender(gen).then(() => { if (payload.added) this.open(); this._loadUpsell(); });
    }
  }

  _abortRender() {
    if (this._renderAbort) {
      try { this._renderAbort.abort(); } catch (e) { /* noop */ }
      this._renderAbort = null;
    }
  }

  /* Self-render the section. `gen` is the repaint token captured by the caller: a
     response that comes back after a newer repaint has started is DISCARDED instead
     of replacing the panel with older HTML (buy-path audit 2026-07-31 measured a
     6s-delayed render rolling a 3-item cart back to 2 items and 99,98 €).
     No timeout here — KK.cart owns request timeouts; a section fetch that hangs is
     superseded (and aborted) by the next mutation. */
  _selfRender(gen) {
    if (!this.sectionId) return Promise.resolve();
    if (gen == null) { gen = ++this._gen; this._abortRender(); }
    const url = `${location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`;
    const ac = (typeof AbortController === 'function') ? new AbortController() : null;
    this._renderAbort = ac;
    return fetch(url, ac ? { signal: ac.signal } : undefined)
      .then((res) => {
        if (!res.ok) throw new Error('section render ' + res.status);
        return res.text();
      })
      .then((text) => {
        if (gen !== this._gen) return; // stale render — a newer one already owns the panel
        this._render(text);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return; // superseded on purpose
        if (gen !== this._gen) return;
        // Never swallow it: the cart mutation already SUCCEEDED server-side, so the
        // panel on screen is now known-wrong (ghost lines, stale subtotal).
        console.warn('[kk] cart drawer: section render failed', err && err.message);
        return this._reconcile(gen);
      })
      .finally(() => { if (this._renderAbort === ac) this._renderAbort = null; });
  }

  /* ─────────────── Reconciliation after a failed repaint ───────────────
     buy-path audit 2026-07-31: "after a failed drawer re-render the slide cart shows
     a ghost line and a subtotal 50 € above the real cart". Order of preference:
       1. re-read the true cart (KK.cart.get → KK.cart.state),
       2. one more attempt at the section render (transient 429/CDN failures dominate),
       3. patch the panel from the cart JSON so it can never show lines the buyer
          already deleted or a total the checkout will contradict. */
  _reconcile(gen) {
    if (gen == null) gen = ++this._gen;
    const getCart = (window.KK && window.KK.cart && window.KK.cart.get)
      ? window.KK.cart.get().catch((err) => {
          console.warn('[kk] cart drawer: cart re-read failed', err && err.code);
          return null;
        })
      : Promise.resolve(null);
    return getCart.then((cart) => {
      if (gen !== this._gen) return;
      return this._retrySection(gen).then((rendered) => {
        if (rendered || gen !== this._gen) return;
        const truth = cart || (window.KK && window.KK.cart && window.KK.cart.state) || null;
        this._patchFromCart(truth);
      });
    });
  }

  _retrySection(gen) {
    if (!this.sectionId) return Promise.resolve(false);
    const url = `${location.pathname}?section_id=${encodeURIComponent(this.sectionId)}&kk_retry=1`;
    return fetch(url)
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        if (!text || gen !== this._gen) return false;
        this._render(text);
        return true;
      })
      .catch((err) => {
        console.warn('[kk] cart drawer: section render retry failed', err && err.message);
        return false;
      });
  }

  /* Last-resort repaint from /cart.js. Only touches values the cart JSON actually
     proves; anything it cannot prove (compare-at savings) is REMOVED rather than
     left on screen as a stale claim. */
  _patchFromCart(cart) {
    const panel = this.dialog.querySelector('[data-cart-drawer-content]');
    // No truth to patch with — say so rather than leave the stale panel unchallenged.
    if (!panel || !cart || !Array.isArray(cart.items)) { this._notify(null); return; }
    const byKey = Object.create(null);
    cart.items.forEach((it) => { byKey[it.key] = it; });

    panel.querySelectorAll('[data-line-key]').forEach((row) => {
      const line = byKey[row.getAttribute('data-line-key')];
      if (!line) { row.remove(); return; } // the buyer already deleted this one
      const qty = row.querySelector('.kk-qty__input');
      if (qty) qty.value = line.quantity;
      // Re-sync the stepper's own disabled state to the value we just wrote. Never
      // dispatch 'change' here — that would post the quantity back to the cart.
      const stepper = row.querySelector('quantity-input');
      if (stepper && typeof stepper._reflectDisabled === 'function') stepper._reflectDisabled();
      const total = row.querySelector('.kk-line__price-final');
      if (total) total.textContent = this._money(line.final_line_price);
      // compare_at_price is not exposed by /cart.js — drop the strike price and the
      // per-line saving rather than leave the pre-mutation numbers next to a new total.
      row.querySelectorAll('.kk-line__price-orig, .kk-line__saving').forEach((el) => el.remove());
    });

    const count = panel.querySelector('[data-cart-count]');
    if (count) count.textContent = cart.item_count;
    const subtotal = panel.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = this._money(cart.total_price);
    // Same reason as the per-line saving: unverifiable from /cart.js. [hidden] loses
    // to the element's own display:flex, so remove it outright.
    const savings = panel.querySelector('.s-cart-drawer__savings');
    if (savings) savings.remove();
    this._patchShipBar(panel, cart.total_price);

    if (!cart.item_count) {
      const footer = panel.querySelector('[data-cart-footer]');
      if (footer) footer.style.display = 'none';
      const scroll = panel.querySelector('[data-cart-scroll]');
      if (scroll) scroll.style.display = 'none';
      this._notice = this.getAttribute('data-empty-text') || null;
      this._paintNotice();
      return;
    }
    // Deliberately NO error banner here: after the patch the lines, quantities,
    // subtotal and ship bar are the real cart, so alarming the buyer on the money path
    // would be a false alarm. The degraded render is logged instead.
    console.warn('[kk] cart drawer: repainted from /cart.js (section render unavailable)');
  }

  _patchShipBar(panel, totalCents) {
    const bar = panel.querySelector('[data-ship-bar]');
    const threshold = parseInt(this.getAttribute('data-ship-threshold'), 10);
    if (!bar || !threshold || threshold <= 0) return;
    const remaining = Math.max(0, threshold - totalCents);
    const pct = Math.min(100, Math.floor((totalCents * 100) / threshold));
    const fill = bar.querySelector('.s-cart-drawer__ship-fill');
    if (fill) fill.style.width = pct + '%';
    const track = bar.querySelector('.s-cart-drawer__ship-track');
    if (track) track.setAttribute('aria-valuenow', String(pct));
    bar.classList.toggle('is-reached', remaining <= 0);
    const text = bar.querySelector('.s-cart-drawer__ship-text');
    if (!text) return;
    if (remaining > 0) {
      // The locale key is an _html string (it wraps the amount in <strong>); the only
      // interpolation is our own formatted number, never buyer input.
      const tpl = this.getAttribute('data-ship-progress-tpl') || '';
      if (tpl) text.innerHTML = tpl.replace('__AMOUNT__', this._money(remaining));
    } else {
      text.textContent = this.getAttribute('data-ship-reached-text') || text.textContent;
    }
  }

  /* Money formatting for the reconcile path ONLY (every healthy render gets its
     numbers from Liquid's `money` filter). Driven by the shop's own money_format,
     handed down on data-money-format, so it cannot drift from the rest of the theme. */
  _money(cents) {
    const fmt = this.getAttribute('data-money-format') || '{{amount}}';
    const value = Number(cents) || 0;
    const build = (decimalSep, thousandSep, decimals) => {
      const fixed = (value / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
      return decimals > 0 ? whole + decimalSep + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
      switch (name) {
        case 'amount_no_decimals': return build('.', ',', 0);
        case 'amount_with_comma_separator': return build(',', '.', 2);
        case 'amount_no_decimals_with_comma_separator': return build(',', '.', 0);
        case 'amount_with_apostrophe_separator': return build('.', '’', 2);
        case 'amount_no_decimals_with_space_separator': return build(',', ' ', 0);
        case 'amount_with_space_separator': return build(',', ' ', 2);
        case 'amount_with_period_and_space_separator': return build('.', ' ', 2);
        default: return build('.', ',', 2); // {{amount}}
      }
    });
  }

  /* ─────────────── Buyer-facing failure notice ───────────────
     Renders ONLY err.userMessage (already-localized German from KK.cart) or the
     section's own generic string — never err.message / raw exception text. Held in
     component state so it survives the panel replacement a reconcile performs. */
  _notify(err) {
    let text = '';
    if (err && typeof err.userMessage === 'string') text = err.userMessage.trim();
    if (!text) text = this.getAttribute('data-error-generic') || '';
    this._notice = text || null;
    this._paintNotice();
  }

  _clearNotice() {
    this._notice = null;
    this._paintNotice();
  }

  _paintNotice() {
    const el = this.dialog ? this.dialog.querySelector('[data-cart-notice]') : null;
    if (!el) return;
    el.textContent = this._notice || '';
    el.hidden = !this._notice;
  }

  _extractSectionHTML(sections) {
    if (!sections) return null;
    // CartStore may key sections by our section id (preferred) or by section type.
    let raw = null;
    if (this.sectionId && sections[this.sectionId] != null) raw = sections[this.sectionId];
    else if (sections['cart-drawer'] != null) raw = sections['cart-drawer'];
    if (raw == null) return null;
    return String(raw);
  }

  _render(rawHTML) {
    const parsed = new DOMParser().parseFromString(rawHTML, 'text/html');
    const nextPanel = parsed.querySelector('[data-cart-drawer-content]');
    const currentPanel = this.dialog.querySelector('[data-cart-drawer-content]');
    if (nextPanel && currentPanel) {
      this._preserveExpress(currentPanel, nextPanel);
      currentPanel.replaceWith(nextPanel);
    }
    this._cacheExpress();
    this._hydrateDiscount();
    this._paintNotice(); // the panel was replaced — re-apply any pending failure text
  }

  /* ─────────────── Dynamic checkout (Shop Pay / PayPal) ───────────────
     content_for_additional_checkout_buttons can come back EMPTY from the Section
     Rendering API (it renders at most once per page). If the re-rendered panel has
     an empty express slot, keep the wallet buttons already initialised in the DOM
     (or the last non-empty copy we cached) so they never blink out on re-render. */
  _expressSlot(root) { return root ? root.querySelector('[data-cart-express]') : null; }

  _cacheExpress() {
    const cur = this._expressSlot(this.dialog);
    if (cur && cur.innerHTML.trim()) this._expressHTML = cur.innerHTML;
  }

  _preserveExpress(currentPanel, nextPanel) {
    const nxt = this._expressSlot(nextPanel);
    if (!nxt || nxt.innerHTML.trim()) return; // next already has buttons — nothing to do
    const cur = this._expressSlot(currentPanel);
    const curHTML = cur && cur.innerHTML.trim() ? cur.innerHTML : null;
    if (curHTML) nxt.innerHTML = curHTML;
    else if (this._expressHTML) nxt.innerHTML = this._expressHTML;
  }

  /* ───────────────────────── Upsell ───────────────────────── */

  _firstProductId() {
    try {
      const items = window.KK && window.KK.cart && window.KK.cart.state && window.KK.cart.state.items;
      if (items && items.length) return items[0].product_id;
    } catch (e) { /* noop */ }
    return null;
  }

  _cartProductIds() {
    const ids = new Set();
    try {
      (window.KK.cart.state.items || []).forEach((i) => ids.add(String(i.product_id)));
    } catch (e) { /* noop */ }
    return ids;
  }

  _upsellContainer() { return this.dialog ? this.dialog.querySelector('[data-cart-upsell]') : null; }

  _loadUpsell() {
    if (!this._upsellEnabled) return;
    if (!this.dialog.open && !this.dialog.hasAttribute('open')) return; // only when visible
    const container = this._upsellContainer();
    if (!container) return;

    const pid = this._firstProductId();
    if (!pid) {
      // Cart state may not be primed yet — prime once, then retry.
      if (!this._primedCart && window.KK && window.KK.cart && window.KK.cart.get) {
        this._primedCart = true;
        window.KK.cart.get()
          .then(() => this._loadUpsell())
          // Non-fatal (the upsell strip is a suggestion, not the buy path) but never
          // silent — buy-path audit 2026-07-31 rule: log AND restore the UI.
          .catch((err) => {
            console.warn('[kk] cart drawer: upsell cart prime failed', err && err.code);
            const c = this._upsellContainer();
            if (c) this._hideUpsell(c);
          });
      } else {
        this._hideUpsell(container);
      }
      return;
    }

    // Same first product as the cached fetch → re-apply cached HTML (cheap; the
    // in-cart filter still removes anything just added).
    if (this._upsellForProduct === String(pid) && this._upsellHTML != null) {
      this._applyUpsell(container, this._upsellHTML);
      return;
    }

    const intents = [this._upsellIntent];
    if (this._upsellIntent !== 'related') intents.push('related'); // complementary → related fallback
    this._fetchUpsellChain(pid, intents, 0, container);
  }

  _fetchUpsellChain(pid, intents, i, container) {
    if (i >= intents.length) {
      this._upsellForProduct = String(pid);
      this._upsellHTML = '';
      this._hideUpsell(container);
      return;
    }
    // Always request the route maximum (10): the section filters out `no-cart-upsell`-tagged
    // products server-side and caps the RENDERED count at upsell_limit, so exclusions must
    // not shrink the candidate pool.
    const url = `${this._recUrl}?section_id=${encodeURIComponent(this.sectionId)}` +
      `&product_id=${encodeURIComponent(pid)}&intent=${encodeURIComponent(intents[i])}&limit=10`;
    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const inner = this._extractUpsellInner(html);
        if (inner && this._itemCount(inner) > 0) {
          this._upsellForProduct = String(pid);
          this._upsellHTML = inner;
          this._applyUpsell(container, inner);
        } else {
          this._fetchUpsellChain(pid, intents, i + 1, container);
        }
      })
      .catch(() => this._fetchUpsellChain(pid, intents, i + 1, container));
  }

  _extractUpsellInner(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const u = doc.querySelector('[data-cart-upsell]');
    return u ? u.innerHTML : null;
  }

  _itemCount(innerHTML) {
    const tmp = document.createElement('div');
    tmp.innerHTML = innerHTML;
    return tmp.querySelectorAll('[data-upsell-item]').length;
  }

  _hideUpsell(container) {
    container.hidden = true;
    container.innerHTML = '';
  }

  _applyUpsell(container, innerHTML) {
    // The container was captured BEFORE the recommendations fetch; a repaint that
    // landed meanwhile detached it and the strip would silently write into nothing
    // (buy-path audit 2026-07-31). Always write into the live panel's container.
    container = this._upsellContainer() || container;
    container.innerHTML = innerHTML;
    // Drop suggestions that are already in the cart.
    const inCart = this._cartProductIds();
    let remaining = 0;
    container.querySelectorAll('[data-upsell-item]').forEach((li) => {
      if (inCart.has(String(li.getAttribute('data-product-id')))) li.remove();
      else remaining += 1;
    });
    if (remaining === 0) { this._hideUpsell(container); return; }
    container.hidden = false;
    this._setupUpsellNav(container);
    this._fireUpsellView(container);
  }

  _setupUpsellNav(container) {
    const track = container.querySelector('[data-upsell-track]');
    const prev = container.querySelector('[data-upsell-prev]');
    const next = container.querySelector('[data-upsell-next]');
    if (!track) return;
    const update = () => {
      const overflow = track.scrollWidth - track.clientWidth > 4;
      [prev, next].forEach((b) => { if (b) b.hidden = !overflow; });
      if (!overflow) return;
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    };
    if (!track._navBound) { track.addEventListener('scroll', update, { passive: true }); track._navBound = true; }
    update();
  }

  _scrollUpsell(btn, dir) {
    const track = btn.closest('.s-cart-drawer__upsell-carousel');
    const el = track ? track.querySelector('[data-upsell-track]') : null;
    if (!el) return;
    const card = el.querySelector('[data-upsell-item]');
    const step = card ? card.getBoundingClientRect().width + 12 : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  _itemFromLi(li) {
    if (!li) return null;
    const priceCents = Number(li.getAttribute('data-item-price'));
    return {
      item_id: li.getAttribute('data-item-id') || '',
      item_name: li.getAttribute('data-item-name') || '',
      price: (isNaN(priceCents) ? 0 : priceCents) / 100,
      quantity: 1
    };
  }

  _currency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) return window.Shopify.currency.active;
    } catch (e) { /* noop */ }
    return 'EUR';
  }

  _fireUpsellView(container) {
    const items = Array.from(container.querySelectorAll('[data-upsell-item]')).map((li) => this._itemFromLi(li)).filter(Boolean);
    if (!items.length) return;
    // Dedupe identical impressions across re-renders: fire once per distinct set
    // of shown products (a changed set = a genuine new impression).
    const sig = items.map((i) => i.item_id).join(',');
    if (this._viewedSig === sig) return;
    this._viewedSig = sig;
    if (!window.KK || !window.KK.track) return;
    window.KK.track('kk_upsell_view', { ecommerce: { currency: this._currency(), item_list_name: 'cart_upsell', items: items } });
  }

  _onUpsellClick(link) {
    const li = link.closest('[data-upsell-item]');
    const item = this._itemFromLi(li);
    if (item && window.KK && window.KK.track) {
      window.KK.track('kk_upsell_click', { ecommerce: { currency: this._currency(), item_list_name: 'cart_upsell', items: [item] } });
    }
  }

  _closeAllDd() {
    this.dialog.querySelectorAll('[data-upsell-dd].is-open').forEach((dd) => {
      dd.classList.remove('is-open');
      const t = dd.querySelector('[data-upsell-dd-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
      const l = dd.querySelector('[data-upsell-dd-list]');
      if (l) { l.hidden = true; l.style.right = ''; }
    });
  }

  _onUpsellAdd(btn) {
    if (!window.KK || !window.KK.cart) return;
    const li = btn.closest('[data-upsell-item]');
    // Multi-variant products render the inline dropdown ([data-upsell-dd],
    // doc 6) — the customer's choice wins over the default variant on the
    // button. Legacy [data-upsell-variant] select kept as fallback.
    const dd = li && li.querySelector('[data-upsell-dd]');
    const sel = li && li.querySelector('select[data-upsell-variant]');
    const variantId = (dd && dd.getAttribute('data-selected-variant')) || (sel && sel.value) || btn.getAttribute('data-variant-id');
    // Guard symmetry (buy-path audit 2026-07-31): read BOTH flags the busy state sets.
    if (!variantId || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    const item = this._itemFromLi(li);

    if (item && window.KK.track) {
      window.KK.track('kk_upsell_add', { ecommerce: { currency: this._currency(), item_list_name: 'cart_upsell', items: [item] } });
    }

    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    btn.classList.add('is-loading');
    this._clearNotice();

    const sections = [this.sectionId, 'header'].filter(Boolean).join(',');
    window.KK.cart
      .add({ id: Number(variantId), quantity: 1, properties: { _upsell: 'cart' } }, { sections: sections })
      .then(() => {
        // kk:cart:updated re-renders the drawer (which re-runs _loadUpsell and
        // filters the just-added product out of the suggestions). Keep it open.
        this.open();
      })
      .catch((err) => {
        // Was an empty catch (buy-path audit 2026-07-31: "an item can be bought with
        // zero feedback" — the add had actually landed and the buyer tapped twice).
        console.warn('[kk] cart drawer: upsell add failed', err && err.code);
        this._notify(err);
        return this._reconcile(++this._gen);
      })
      .finally(() => {
        if (btn.isConnected) {
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }
      });
  }

  /* ───────────────────────── Discount code ─────────────────────────
     Hitting /discount/CODE primes the discount cookie so the code is picked up at
     checkout. It does NOT tell us whether the code exists: the buy-path audit
     (2026-07-31) measured GET /discount/GIBTESNICHT99XYZ answering HTTP 200 and
     redirecting to the homepage, so neither the status code nor a resolved fetch
     carries any signal. The drawer used to treat every resolved fetch as success and
     show a green "wird an der Kasse angewendet" chip — a price promise the checkout
     then refuses — and persisted that claim in sessionStorage across reloads.

     It now NEVER asserts more than it can prove, in two steps:
       1. prime the cookie,
       2. re-read the cart (KK.cart.get) and look for the code in the cart's own
          discount data.
     Found  → CONFIRMED: the green "applied" chip, which is now a proven claim.
     Not found → PENDING: neutral wording ("… wird an der Kasse geprüft"), no success
          styling. Pending is not the same as invalid — gift-card codes, free-shipping
          codes and codes with an order minimum legitimately never surface in
          /cart.js — so the drawer must not call them wrong either.
     The persisted value is the CODE ONLY; the claim is re-derived from live cart state
     on every hydrate, so a stale "applied" chip can no longer survive a re-render or a
     reload. The code is always rendered with textContent (it is buyer input). */
  _discountForm() { return this.dialog ? this.dialog.querySelector('[data-discount-form]') : null; }

  _applyDiscount(form) {
    const input = form.querySelector('[data-discount-input]');
    const apply = form.querySelector('[data-discount-apply]');
    const code = ((input && input.value) || '').trim();
    if (!code) { this._discountMsg(form, form.getAttribute('data-error') || '', 'error'); return; }
    // Guard symmetry: the busy state sets the class AND disables the button; both are
    // read here (buy-path audit 2026-07-31).
    if (form.classList.contains('is-loading') || (apply && apply.disabled)) return;
    form.classList.add('is-loading');
    if (apply) { apply.disabled = true; apply.setAttribute('aria-busy', 'true'); }
    // No timeout here — KK.cart owns request timeouts and this priming call is a plain
    // navigation-style GET whose result we do not trust either way.
    fetch('/discount/' + encodeURIComponent(code), { method: 'GET', credentials: 'same-origin' })
      // Deliberately status-blind: a 404/302 does not mean the code is bad and a 200
      // does not mean it is good. Only the cart read below decides.
      .then(() => this._verifyDiscount(code))
      .then((state) => {
        try { sessionStorage.setItem('kk:discount', code); } catch (e) { /* noop */ }
        this._renderDiscountState(form, code, state);
      })
      .catch((err) => {
        // NETWORK failure — distinct from "you typed nothing". The old code reused the
        // empty-input string and told buyers "Bitte gib einen Rabattcode ein." while
        // their code was sitting in the box.
        console.warn('[kk] cart drawer: discount priming failed', err && err.message);
        this._discountMsg(form, form.getAttribute('data-network-error') || form.getAttribute('data-error') || '', 'error');
      })
      .finally(() => {
        form.classList.remove('is-loading');
        if (apply) { apply.disabled = false; apply.removeAttribute('aria-busy'); }
      });
  }

  /* Resolves to 'confirmed' when the live cart really carries this code, else
     'pending'. A failed cart read is 'pending' too — unknown is never "applied". */
  _verifyDiscount(code) {
    if (!(window.KK && window.KK.cart && window.KK.cart.get)) return Promise.resolve('pending');
    return window.KK.cart.get()
      .then((cart) => (this._cartHasDiscount(cart, code) ? 'confirmed' : 'pending'))
      .catch((err) => {
        console.warn('[kk] cart drawer: discount verification failed', err && err.code);
        return 'pending';
      });
  }

  _cartHasDiscount(cart, code) {
    if (!cart || !code) return false;
    const wanted = String(code).trim().toLowerCase();
    const hit = (value) => value != null && String(value).trim().toLowerCase() === wanted;
    try {
      if (Array.isArray(cart.discount_codes) &&
          cart.discount_codes.some((d) => d && hit(d.code || d.title) && d.applicable !== false)) return true;
      if (Array.isArray(cart.cart_level_discount_applications) &&
          cart.cart_level_discount_applications.some((d) => d && hit(d.title || d.code || d.key))) return true;
      if (Array.isArray(cart.items) && cart.items.some((it) =>
        (it && it.line_level_discount_allocations || []).some((a) =>
          a && a.discount_application && hit(a.discount_application.title || a.discount_application.code)))) return true;
    } catch (e) { /* noop */ }
    return false;
  }

  _removeDiscount() {
    try { sessionStorage.removeItem('kk:discount'); } catch (e) { /* noop */ }
    const form = this._discountForm();
    if (form) this._resetDiscount(form);
    // The clear route is best-effort — the audit measured GET /discount/?redirect=…
    // answering 404 on this store, so the cookie can survive. Never claim a silent
    // success: re-read the cart and, if the code is demonstrably still on it, say so.
    fetch('/discount/?redirect=%2Fcart.js', { method: 'GET', credentials: 'same-origin' })
      .catch((err) => { console.warn('[kk] cart drawer: discount clear failed', err && err.message); })
      .then(() => {
        if (!(window.KK && window.KK.cart && window.KK.cart.get)) return null;
        return window.KK.cart.get().catch((err) => {
          console.warn('[kk] cart drawer: cart re-read after discount removal failed', err && err.code);
          return null;
        });
      })
      .then((cart) => {
        const f = this._discountForm();
        if (!f || !cart) return;
        const stillThere = (cart.cart_level_discount_applications || []).length > 0 ||
          (Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0);
        if (stillThere) this._discountMsg(f, f.getAttribute('data-remove-failed') || '', 'error');
      });
  }

  _discountMsg(form, text, type) {
    const msg = form.querySelector('[data-discount-msg]');
    if (!msg) return;
    // An error and "applied" styling must never coexist (buy-path audit 2026-07-31).
    if (type === 'error') form.classList.remove('is-applied', 'is-pending');
    msg.textContent = text;
    msg.className = 's-cart-drawer__discount-msg' + (type ? ' is-' + type : '');
    msg.hidden = !text;
  }

  _resetDiscount(form) {
    const msg = form.querySelector('[data-discount-msg]');
    if (msg) { msg.textContent = ''; msg.className = 's-cart-drawer__discount-msg'; msg.hidden = true; }
    form.classList.remove('is-applied');
    form.classList.remove('is-pending');
    const input = form.querySelector('[data-discount-input]');
    if (input) input.value = '';
  }

  /* state: 'confirmed' (proven against the cart) | 'pending' (cookie primed, validity
     decided at checkout). Only 'confirmed' gets the success wording + green styling. */
  _renderDiscountState(form, code, state) {
    const confirmed = state === 'confirmed';
    const input = form.querySelector('[data-discount-input]');
    if (input) input.value = '';
    const msg = form.querySelector('[data-discount-msg]');
    if (!msg) return;
    const tpl = (confirmed
      ? form.getAttribute('data-applied-tpl')
      : form.getAttribute('data-pending-tpl')) || '__CODE__';
    const removeLabel = form.getAttribute('data-remove-label') || '';
    msg.textContent = '';
    msg.className = 's-cart-drawer__discount-msg ' + (confirmed ? 'is-applied' : 'is-pending');
    const span = document.createElement('span');
    span.textContent = tpl.replace('__CODE__', code); // textContent: code is buyer input
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 's-cart-drawer__discount-remove';
    btn.setAttribute('data-discount-remove', '');
    btn.setAttribute('aria-label', removeLabel);
    btn.textContent = '×'; // ×
    msg.appendChild(span);
    msg.appendChild(btn);
    msg.hidden = false;
    form.classList.toggle('is-applied', confirmed);
    form.classList.toggle('is-pending', !confirmed);
  }

  /* Runs after every panel replacement and on load. The stored value is only the
     code — the claim is re-derived from the cart we currently hold, so a chip that
     was confirmed once can never keep asserting success after the discount is gone. */
  _hydrateDiscount() {
    const form = this._discountForm();
    if (!form) return;
    let code = null;
    try { code = sessionStorage.getItem('kk:discount'); } catch (e) { /* noop */ }
    if (!code) return;
    const cart = (window.KK && window.KK.cart && window.KK.cart.state) || null;
    this._renderDiscountState(form, code, this._cartHasDiscount(cart, code) ? 'confirmed' : 'pending');
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
